import { formatCurrency, formatDateTime } from '@eriko/shared-types';
import { Building2 } from 'lucide-react';

interface Ref {
  name?: string;
  value?: string;
}

interface MetaData {
  CreateTime?: string;
  LastUpdatedTime?: string;
}

interface Addr {
  Line1?: string;
  Line2?: string;
  Line3?: string;
  Line4?: string;
  City?: string;
  CountrySubDivisionCode?: string;
  PostalCode?: string;
}

/**
 * Renders a Vendor or Customer `entity-detail` artifact (QBO/Xero-shaped
 * click-to-view payload from loadEntityDetail). Both contact kinds share the
 * same card; the eyebrow label and the 1099 badge differ.
 */
export function ContactDetailReport({ data }: { data: Record<string, unknown> }) {
  const kind = data.entityType === 'Customer' ? 'Customer' : 'Vendor';
  const name = String(
    data.DisplayName ?? data.PrintOnCheckName ?? data.CompanyName ?? data.Name ?? '—',
  );
  const companyName = data.CompanyName ? String(data.CompanyName) : '';
  const provider = data.provider ? String(data.provider) : '';
  const externalId = String(data.externalId ?? data.Id ?? '—');
  const active = data.Active !== false;
  const is1099 = data.Vendor1099 === true;

  const currency = data.CurrencyRef as Ref | undefined;
  const currencyName = currency?.name ?? 'United States Dollar';
  const currencyCode = currency?.value ?? '';

  const balance = Number(data.Balance ?? 0);
  const isNegative = balance < 0;

  const email =
    (data.PrimaryEmailAddr as { Address?: string } | undefined)?.Address ??
    (data.EmailAddress ? String(data.EmailAddress) : '');
  const phone = (data.PrimaryPhone as { FreeFormNumber?: string } | undefined)?.FreeFormNumber ?? '';

  const addr = data.BillAddr as Addr | undefined;
  const addressLines: string[] = [];
  if (addr) {
    if (addr.Line1) addressLines.push(addr.Line1);
    if (addr.Line2) addressLines.push(addr.Line2);
    if (addr.Line3) addressLines.push(addr.Line3);
    if (addr.City || addr.CountrySubDivisionCode || addr.PostalCode) {
      addressLines.push(
        [addr.City, addr.CountrySubDivisionCode, addr.PostalCode].filter(Boolean).join(', '),
      );
    }
  }

  const meta = data.MetaData as MetaData | undefined;
  const createTime = meta?.CreateTime ?? '';
  const lastUpdated = meta?.LastUpdatedTime ?? '';

  const detailRows: Array<[string, string]> = [];
  if (companyName && companyName !== name) detailRows.push(['Company', companyName]);
  if (email) detailRows.push(['Email', email]);
  if (phone) detailRows.push(['Phone', phone]);
  detailRows.push([
    'Currency',
    currencyCode ? `${currencyName} (${currencyCode})` : currencyName,
  ]);
  detailRows.push([`${kind} ID`, externalId]);
  if (provider) detailRows.push(['Source', provider]);

  return (
    <div className="overflow-hidden rounded-[10px] border border-surface-200 bg-surface-0 px-8 py-7">
      {/* Header: contact identity + balance */}
      <div className="flex items-start justify-between gap-8">
        <div className="flex-1 min-w-0">
          <div className="mb-2 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-surface-400">
            <Building2 className="h-3.5 w-3.5" strokeWidth={1.5} />
            {kind}
          </div>
          <div
            className="text-[28px] leading-tight text-surface-900"
            style={{ fontFamily: '"Instrument Serif", Georgia, serif', fontWeight: 400 }}
          >
            {name}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            <span className="inline-flex items-center rounded-full bg-surface-100 px-2.5 py-0.5 text-xs text-surface-600">
              {active ? 'Active' : 'Inactive'}
            </span>
            {is1099 && (
              <span className="inline-flex items-center rounded-full bg-surface-100 px-2.5 py-0.5 text-xs text-surface-600">
                1099 vendor
              </span>
            )}
          </div>
        </div>

        <div className="text-right shrink-0">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-surface-400">
            {kind === 'Vendor' ? 'Open Balance' : 'Balance'}
          </div>
          <div
            className={`font-mono text-[28px] leading-none tabular-nums ${
              isNegative ? 'text-neg' : 'text-surface-900'
            }`}
          >
            {formatCurrency(balance, 2)}
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="my-7 border-t border-surface-200" />

      {/* Detail grid */}
      <dl className="grid grid-cols-2 gap-x-8 gap-y-5">
        {detailRows.map(([label, value]) => (
          <div key={label}>
            <dt className="mb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-surface-400">
              {label}
            </dt>
            <dd className="text-sm text-surface-900 break-words">{value}</dd>
          </div>
        ))}
      </dl>

      {/* Address */}
      {addressLines.length > 0 && (
        <>
          <div className="my-7 border-t border-surface-200" />
          <div>
            <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-surface-400">
              Address
            </div>
            <div className="text-sm leading-relaxed text-surface-700">
              {addressLines.map((line, i) => (
                <span key={i}>
                  {line}
                  {i < addressLines.length - 1 && <br />}
                </span>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Footer: timestamps */}
      {(createTime || lastUpdated) && (
        <>
          <div className="mt-7 mb-5 border-t border-surface-200" />
          <div className="flex flex-wrap gap-x-12 gap-y-4">
            {createTime && (
              <div>
                <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-surface-400">
                  Created
                </div>
                <div className="text-sm text-surface-900">{formatDateTime(createTime)}</div>
              </div>
            )}
            {lastUpdated && (
              <div>
                <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-surface-400">
                  Last Updated
                </div>
                <div className="text-sm text-surface-900">{formatDateTime(lastUpdated)}</div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
